import DrawerPortal from 'components/drawer/DrawerPortal';
import { CirclePlayIcon } from 'components/icons';
import { EllipsisTooltip } from 'components/misc/EllipsisTooltip';
import TrackingTimer from 'components/sidebar-tracker/TrackingTimer';
import useTrackedTimecard from 'components/sidebar-tracker/useTrackedTimecard';
import { Button } from 'components/ui/button';
import FinishTimecardIconButton from 'pages/timecards/buttons/FinishTimecardIconButton';
import TimecardTrackingButton from 'pages/timecards/buttons/TimecardTrackingButton';
import TimecardCenterContent from 'pages/timecards/timecard-center/TimecardCenterContent';
import { useDispatch, useSelector } from 'store';
import { toggleSubDrawer } from 'store/reducers/subdrawer';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';

export const TIMECARD_CENTER_DRAWER_ID = 'timecard-center';

// The chip's pause and finish are the timecard pages' round buttons, drawn here as small white circles.
const CHIP_BUTTONS =
    '[&_[data-slot=button]]:bg-paper [&_[data-slot=button]]:text-primary-ink [&_[data-slot=button]]:size-[26px] [&_[data-slot=button]_svg]:size-3.5!';

/**
 * The top bar's timer: the running timecard as a chip — elapsed time, case number and title, pause and
 * finish — or a Start timer button when none runs. Either opens the Timecard center, which closes through its
 * backdrop, as the classic sidebar's does. It hosts the timer itself, so the time ticks and the tab
 * title follows it on every page. Under 768px the case and the button's label are for assistive tech only,
 * and the time never shrinks.
 */
function TimerControl() {
    const dispatch = useDispatch();
    const timecard = useTrackedTimecard();
    const isRunning = useSelector((state) => state.timecard.isStopwatchRunning);
    const openTimecardCenter = () => dispatch(toggleSubDrawer(TIMECARD_CENTER_DRAWER_ID));

    return (
        <>
            <DrawerPortal
                content={<TimecardCenterContent />}
                drawerId={TIMECARD_CENTER_DRAWER_ID}
                titleId="timecards.start-tracking"
                width="100%"
                fitsContent
                type="sub"
            />
            {timecard ? (
                <div
                    className={cn(
                        // Pause and finish are 26px: the gap lets their 32px areas, 44px on touch, meet.
                        'border-primary-wash-border bg-primary-wash text-primary-ink flex h-9 min-w-0 items-center gap-1.5 rounded-full border ps-3 pe-1.5 pointer-coarse:gap-4.5',
                        CHIP_BUTTONS
                    )}
                >
                    <button
                        type="button"
                        onClick={openTimecardCenter}
                        className="hit-area relative me-1 flex min-w-0 cursor-pointer items-center gap-2"
                    >
                        <span
                            aria-hidden
                            className={cn('size-2 flex-none rounded-full', isRunning ? 'bg-running' : 'bg-grey-400')}
                        />
                        <span className="text-meta shrink-0 font-medium tabular-nums">
                            <TrackingTimer timecard={timecard} />
                        </span>
                        {/* Above the button's hit area, which would otherwise take the hover its tooltip opens on. */}
                        <span className="text-primary-ink-muted relative z-1 max-w-60 min-w-0 max-sm:sr-only">
                            <EllipsisTooltip>
                                {timecard.matter.caseNumber} · {timecard.matter.name}
                            </EllipsisTooltip>
                        </span>
                    </button>
                    <TimecardTrackingButton timecard={timecard} />
                    <FinishTimecardIconButton timecard={timecard} />
                </div>
            ) : (
                <Button variant="ghost" className="max-sm:px-2" onClick={openTimecardCenter}>
                    <CirclePlayIcon data-icon="inline-start" />
                    <span className="max-sm:sr-only">{t('menu.start-timer')}</span>
                </Button>
            )}
        </>
    );
}

export default TimerControl;
